import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useApiWakeUp } from '../hooks/useApiWakeUp';
import { WakingOverlay } from '../components/WakingOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const apiStatus = useApiWakeUp();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
    } catch {
      // Endpoint always returns 200 for security — fall through to confirmation
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center p-6" data-testid="forgot-password-page">
      {apiStatus === 'waking' && <WakingOverlay />}
      <Card className="w-full max-w-md border-2 border-[#8B7355]/20">
        <CardHeader>
          <CardTitle className="font-serif text-3xl text-[#8B7355] italic text-center">
            Reset Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#C9A961]/20 flex items-center justify-center">
                <Mail className="w-7 h-7 text-[#C9A961]" />
              </div>
              <p className="text-[#8B7355] text-lg">Check your inbox</p>
              <p className="text-[#8B7355]/70 text-sm">
                If an account exists for <strong>{email}</strong>, we&apos;ve sent a one-time reset link.
                The link expires in 1 hour.
              </p>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-2 text-[#C9A961] hover:text-[#8B7355] mt-4"
                data-testid="forgot-back-to-login"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              <p className="text-sm text-[#8B7355]/80 mb-4">
                Enter your admin email and we&apos;ll send you a one-time link to choose a new password.
              </p>

              <div>
                <Label htmlFor="email" className="flex items-center gap-2 text-[#8B7355] mb-2">
                  <Mail className="w-4 h-4" /> Email
                </Label>
                <Input
                  id="email"
                  name="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="off"
                  className="border-[#8B7355]/30"
                  placeholder="your@email.com"
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#8B7355] hover:bg-[#6B5845] text-white py-6 rounded-none font-serif text-lg"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <div className="text-center pt-2">
                <Link
                  to="/admin/login"
                  className="text-sm text-[#8B7355]/70 hover:text-[#8B7355] inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
